import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {User, UsersState, ImportStep} from '../../types/manageUsers';

const initialState: UsersState = {
    users: [],
    initialUsers: [],
    loading: false,
    importLoading: false,
    error: null,
    importError: null,
    importSuccess: false,
    summary: null,
    importStep: ImportStep.USER_SELECTION,
    userEmails: {},
};

export const manageUsersSlice = createSlice({
    name: 'manageUsers',
    initialState,
    reducers: {
        setUsersLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setUsersError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        setUsers: (state, action: PayloadAction<{ users: User[], summary: UsersState['summary'] }>) => {
            // Ensure all users have a consistent selected state (default to false if not provided)
            const usersWithSelection = action.payload.users.map(user => ({
                ...user,
                selected: user.selected || false
            }));
            
            state.users = usersWithSelection;
            state.initialUsers = usersWithSelection.map(user => ({ ...user })); // Deep copy of initial state
            state.summary = action.payload.summary;
        },
        setImportUsersLoading: (state, action: PayloadAction<boolean>) => {
            state.importLoading = action.payload;
        },
        setImportUsersError: (state, action: PayloadAction<string | null>) => {
            state.importError = action.payload;
        },
        setImportUsersSuccess: (state, action: PayloadAction<boolean>) => {
            state.importSuccess = action.payload;
        },
        toggleUserSelection: (state, action: PayloadAction<string>) => {
            const userId = action.payload;
            state.users = state.users.map(user => 
                user.id === userId 
                    ? { ...user, selected: !user.selected }
                    : user
            );
        },
        toggleSelectAllUsers: (state) => {
            const allSelected = state.users.every(user => user.selected);
            state.users = state.users.map(user => ({ ...user, selected: !allSelected }));
        },
        clearUsers: (state) => {
            state.users = [];
            state.initialUsers = [];
            state.summary = null;
            state.error = null;
            state.importError = null;
            state.importSuccess = false;
        },
        resetImportState: (state) => {
            state.importLoading = false;
            state.importError = null;
            state.importSuccess = false;
        },
        setImportStep: (state, action: PayloadAction<ImportStep>) => {
            state.importStep = action.payload;
        },
        setUserEmail: (state, action: PayloadAction<{userId: string, email: string}>) => {
            state.userEmails[action.payload.userId] = action.payload.email;
        },
        setMultipleUserEmails: (state, action: PayloadAction<Record<string, string>>) => {
            state.userEmails = { ...state.userEmails, ...action.payload };
        },
        clearUserEmails: (state) => {
            state.userEmails = {};
        },
        resetImportFlow: (state) => {
            state.importStep = ImportStep.USER_SELECTION;
            state.userEmails = {};
            state.importLoading = false;
            state.importError = null;
            state.importSuccess = false;
        },
        removeImportedUsers: (state, action: PayloadAction<string[]>) => {
            const userIds = action.payload;
            // Remove the imported users from the list
            state.users = state.users.filter(user => !userIds.includes(user.id));
            state.initialUsers = state.initialUsers.filter(user => !userIds.includes(user.id));
        },
    },
});

export const {
    setUsersLoading,
    setUsersError,
    setUsers,
    setImportUsersLoading,
    setImportUsersError,
    setImportUsersSuccess,
    toggleUserSelection,
    toggleSelectAllUsers,
    clearUsers,
    resetImportState,
    setImportStep,
    setUserEmail,
    setMultipleUserEmails,
    clearUserEmails,
    resetImportFlow,
    removeImportedUsers,
} = manageUsersSlice.actions;

export default manageUsersSlice.reducer;